import { api } from "./api-client";
import type { Locale } from "./i18n/config";
import { internalMetricsEnabled } from "./tracking/track";
import type { RankingScope, TimeRankingMode, TimeRankingPeriod, TimeRankingResult } from "./daily-word";

// "Sopa de letras": 3 vidas al día (una por partida) y 3 niveles. La sopa,
// el reloj y las vidas los controla el servidor (Render, sin pasar por
// functions de Netlify); sin sesión, el progreso viaja en un comprobante
// firmado guardado en este navegador.

export type Cell = [number, number];
export type PlayStatus = "playing" | "won" | "lost";

export interface LevelInfo {
  level: number;
  size: number;
  words: number;
  timeLimitMs: number;
}

export interface WordEvent {
  slug: string;
  title: string;
}

export interface WordView {
  display: string;
  length: number;
  found: boolean;
  event?: WordEvent;
}

export interface FoundWord {
  index: number;
  from: Cell;
  to: Cell;
}

export interface PlayView {
  level: number;
  size: number;
  rows: string[];
  words: WordView[];
  found: FoundWord[];
  status: PlayStatus;
  startedAt: string;
  deadline: string;
  // Lo que tardó (solo si la ganó): su marca para el ranking.
  timeMs?: number;
}

export interface WordSearchState {
  day: number;
  nextChangeAt: string;
  maxLives: number;
  livesLeft: number;
  nextLevel: number | null;
  wonLevels: number[];
  levels: LevelInfo[];
  active: PlayView | null;
  last: PlayView | null;
  token?: string;
  // Hoy ya jugó en otro idioma (una sopa al día, 3 intentos en total).
  playedIn?: Locale;
}

export interface FindResponse extends WordSearchState {
  found: { index: number; display: string; event?: WordEvent } | null;
}

interface Body {
  lang: Locale;
  country: string;
  token?: string;
}

// Métricas internas: el server no cuenta los navegadores del equipo ni los
// de Staging.
function withMeasure<T extends object>(body: T): T & { platform: "web"; internal: boolean } {
  return { ...body, platform: "web", internal: !internalMetricsEnabled() };
}

function path(loggedIn: boolean, action: string): string {
  return `/word-search/${loggedIn ? "me/" : ""}${action}`;
}

// Ranking por nivel (mismo perfil de jugador que la palabra del día).
export function fetchWordSearchRanking(
  params: {
    lang: Locale;
    country: string;
    scope: RankingScope;
    level: number;
    mode: TimeRankingMode;
    period: TimeRankingPeriod;
  },
  loggedIn: boolean,
): Promise<TimeRankingResult & { level: number }> {
  const query = new URLSearchParams({ ...params, level: String(params.level) }).toString();
  return api.get<TimeRankingResult & { level: number }>(`/word-search/${loggedIn ? "me/" : ""}ranking?${query}`);
}

export function fetchWordSearchState(loggedIn: boolean, body: Body): Promise<WordSearchState> {
  return api.post<WordSearchState>(path(loggedIn, "state"), withMeasure(body));
}

export function startWordSearchLevel(loggedIn: boolean, body: Body): Promise<WordSearchState> {
  return api.post<WordSearchState>(path(loggedIn, "start"), withMeasure(body));
}

export function sendWordSearchFind(
  loggedIn: boolean,
  body: Body & { from: Cell; to: Cell },
): Promise<FindResponse> {
  return api.post<FindResponse>(path(loggedIn, "find"), withMeasure(body));
}

// Casillas de una selección en línea recta (horizontal, vertical o
// diagonal); si no es recta, la línea más cercana desde el inicio.
export function lineCells(from: Cell, to: Cell): Cell[] {
  let dr = to[0] - from[0];
  let dc = to[1] - from[1];
  if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) {
    // Se ajusta a la dirección dominante (o a la diagonal si están cerca).
    if (Math.abs(Math.abs(dr) - Math.abs(dc)) <= Math.min(Math.abs(dr), Math.abs(dc))) {
      const n = Math.max(Math.abs(dr), Math.abs(dc));
      dr = Math.sign(dr) * n;
      dc = Math.sign(dc) * n;
    } else if (Math.abs(dr) > Math.abs(dc)) {
      dc = 0;
    } else {
      dr = 0;
    }
  }
  const steps = Math.max(Math.abs(dr), Math.abs(dc));
  return Array.from({ length: steps + 1 }, (_, i): Cell => [
    from[0] + Math.sign(dr) * i,
    from[1] + Math.sign(dc) * i,
  ]);
}

// Comprobante sin sesión (el del día anterior lo descarta el server). Un solo comprobante, en cualquier idioma: el servidor no deja jugar en otro
// idioma si hoy ya se jugó (una sopa al día).
const STORAGE_KEY = "cv_word_search";

export function loadWordSearchToken(): string | undefined {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function saveWordSearchToken(token: string | undefined): void {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Modo privado / storage bloqueado: el progreso dura solo esta visita.
  }
}
